/**
 * The profile's head: who this is, what to do about them, and how the
 * relationship stands.
 *
 * The hero names the contact and offers the four things a desk does next:
 * write, call, note, open a deal. The health panel beside it counts the
 * relationship from dated records. The next-best-action card below is the one
 * element on the page with a gold outline, and appears only when a stated rule
 * fires.
 */
import { useState, type ReactNode } from 'react'
import clsx from 'clsx'
import { Building2, Mail, PhoneCall, StickyNote } from 'lucide-react'

import { type CrmContact } from '@/services/crm'
import type { RelationshipDefinitions, RelationshipRow } from '@/services/crmRelationships'
import { formatUsd } from '@/utils/money'
import { mailtoHref, openDeal, telHref } from './contactLinks'
import { contactInitials } from './crmFields'
import {
  agoLabel,
  formatDay,
  touchPhrase,
  type AddressGroups,
  type NextAction,
  type RelationshipFacts,
  count,
} from './profileFacts'
import HealthRing from './relationships/HealthRing'
import { BAND_STYLE, STAGE_STYLE } from './relationships/relationshipFormat'
import { Absent, Avatar, FOCUS, QUIET_BUTTON } from './profileUi'

export const HERO_ACTION =
  'inline-flex h-9 items-center gap-2 rounded-lg border border-stone-600 bg-black/40 px-3.5 text-[13px] font-medium text-stone-100 hover:border-amber-400/50 hover:text-white ' +
  FOCUS
const ACTION_OFF =
  'inline-flex h-9 cursor-not-allowed items-center gap-2 rounded-lg border border-stone-800 px-3.5 text-[13px] font-medium text-stone-600'

const CHIP = 'rounded-full border px-2.5 py-0.5 text-[11px]'

export function ProfileHero({
  contact,
  subtitle,
  groups,
  stage,
  chips,
  dealsMenu,
  onLogNote,
}: {
  contact: CrmContact
  subtitle: string
  groups: AddressGroups
  /** The board's stage for this contact, when the board has one. */
  stage?: RelationshipRow['stage']
  /** Chips that act on the record (Needs review, Retired) live with the window's mutations. */
  chips: ReactNode
  dealsMenu: ReactNode
  onLogNote: () => void
}) {
  const entity = contact.kind === 'organisation' || Boolean(contact.isEntity)
  const mailHref = mailtoHref(groups.primary)
  const phone = contact.phones?.primary ?? contact.phone
  const callHref = telHref(phone)
  const first = contact.firstName?.trim()
  const emailLabel = !entity && first ? `Email ${first}` : 'Email'

  return (
    <div className="flex min-w-0 items-center gap-5">
      <Avatar size="lg" square={entity}>
        {entity ? <Building2 size={28} /> : contactInitials(contact)}
      </Avatar>
      <div className="min-w-0 space-y-2">
        <h2
          className={clsx(
            'break-words font-display text-[40px] font-semibold leading-[1.02]',
            contact.retired ? 'text-stone-400 line-through decoration-stone-600' : 'text-stone-50',
          )}
        >
          {contact.displayName}
        </h2>
        <div className="text-[14px] text-stone-400">
          {subtitle || <span className="italic text-stone-600">Title, company and location not on record</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Neutral on purpose: the type is a label, not a signal. Gold is for the one action. */}
          <span className={clsx(CHIP, 'border-stone-600 bg-black/30 text-stone-200')}>{contact.companyType}</span>
          {entity ? <span className={clsx(CHIP, 'border-stone-700 text-stone-400')}>Organization</span> : null}
          {stage ? (
            // The board's own stage styling: the dot colour is the second cue, the word is the first.
            <span className={clsx(CHIP, 'inline-flex items-center gap-1.5 border-stone-600 bg-black/30', STAGE_STYLE[stage].text)}>
              <span aria-hidden className={clsx('h-2 w-2 rounded-full', STAGE_STYLE[stage].dot)} />
              {STAGE_STYLE[stage].title}
            </span>
          ) : null}
          {contact.isInternal && contact.companyType !== 'Internal' ? (
            <span className={clsx(CHIP, 'border-stone-600 text-stone-300')}>Internal</span>
          ) : null}
          {chips}
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          {mailHref ? (
            <a href={mailHref} className={HERO_ACTION}>
              <Mail size={14} aria-hidden /> {emailLabel}
            </a>
          ) : (
            <button type="button" disabled className={ACTION_OFF} title="No mailbox is on record for this contact">
              <Mail size={14} aria-hidden /> {emailLabel}
            </button>
          )}
          {callHref ? (
            <a href={callHref} className={HERO_ACTION} aria-label="Call" title="Dial the number on record">
              <PhoneCall size={14} aria-hidden /> Call
            </a>
          ) : (
            <button type="button" disabled className={ACTION_OFF} title="No phone number is on record for this contact">
              <PhoneCall size={14} aria-hidden /> Call
            </button>
          )}
          <button type="button" onClick={onLogNote} className={HERO_ACTION}>
            <StickyNote size={14} aria-hidden /> Log a note
          </button>
          {dealsMenu}
        </div>
      </div>
    </div>
  )
}

// ── Relationship health ───────────────────────────────────────────────────────

function Figure({ label, value, note }: { label: string; value: ReactNode; note: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-stone-500">{label}</dt>
      <dd className="mt-0.5 whitespace-nowrap text-[18px] font-semibold leading-tight tabular-nums text-stone-100">{value}</dd>
      <dd className="mt-0.5 text-[11.5px] leading-snug text-stone-400">{note}</dd>
    </div>
  )
}

export interface BoardRelationship {
  row: RelationshipRow
  definitions: RelationshipDefinitions
}

/**
 * Plain, labelled figures counted from dated records, and, when the
 * relationships board has scored this contact, the board's ring, stage and the
 * three parts of its score. The profile computes no score of its own: without
 * the board there is no ring, only the figures.
 */
export function RelationshipHealth({
  facts,
  relationship,
  organization = false,
}: {
  facts: RelationshipFacts
  relationship?: BoardRelationship | null
  /** An organization is never scored: its deals are its people's deals rolled up. */
  organization?: boolean
}) {
  const [rule, setRule] = useState(false)
  const last = facts.daysSinceLastTouch
  const row = relationship?.row ?? null
  const parts = relationship?.definitions.health.parts ?? []
  return (
    <section aria-label="Relationship health" className="rounded-xl border border-stone-800 bg-black/35 p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="font-sans text-[14px] font-semibold text-stone-100">Relationship health</h3>
        <span className="text-[11px] text-stone-500">Counted from dated messages and deals</span>
      </div>
      <div className="flex items-center gap-5">
        {row && row.health !== null ? (
          <div className="shrink-0 text-center">
            <HealthRing row={row} size={92} />
            {row.healthBand ? <div className={clsx('mt-0.5 text-[11px]', BAND_STYLE[row.healthBand].text)}>{BAND_STYLE[row.healthBand].word}</div> : null}
          </div>
        ) : null}
        {/* Beside a ring there is room for two columns of figures, not four. */}
        <dl className={clsx('grid min-w-0 flex-1 grid-cols-2 gap-x-5 gap-y-4', !(row && row.health !== null) && 'lg:grid-cols-4')}>
          <Figure
            label="Last activity"
            value={last === null ? <Absent>None dated</Absent> : last < 1 ? 'Today' : agoLabel(last)}
            note={facts.lastTouchAt ? `${capitalize(touchPhrase(facts.lastTouchKind))}, ${formatDay(facts.lastTouchAt)}` : 'No dated message or deal'}
          />
          <Figure
            label="Deals, 12 months"
            value={facts.dealsLast12Months}
            note={
              <>
                {count(facts.dealsLast90Days, 'deal')} in the last 90 days
                {facts.undatedDeals > 0 ? `; ${facts.undatedDeals} undated` : ''}
              </>
            }
          />
          <Figure
            label="Volume, 12 months"
            value={facts.volumeLast12Months !== null ? formatUsd(facts.volumeLast12Months) : <Absent>No amounts</Absent>}
            note={
              facts.dealsLast12Months === 0
                ? 'No deals in the window'
                : `Amount on ${facts.volumeAmountsKnown} of ${facts.dealsLast12Months}`
            }
          />
          <Figure
            label="Messages"
            value={facts.messagesTotal}
            note={`${count(facts.messagesLast90Days, 'message')} in the last 90 days`}
          />
        </dl>
      </div>
      {row ? (
        <div className="mt-3 border-t border-stone-800/80 pt-3">
          {row.reason ? <p className="text-[12px] leading-snug text-stone-300">{row.reason}</p> : null}
          {row.healthParts && parts.length > 0 ? (
            <ul aria-label="Parts of the score" className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[11.5px] text-stone-400">
              {parts.map((part) => (
                <li key={part.key}>
                  {part.label}{' '}
                  <span className="tabular-nums text-stone-200">
                    {Math.round(row.healthParts?.[part.key] ?? 0)} of {part.max}
                  </span>
                </li>
              ))}
            </ul>
          ) : row.health === null ? (
            <p className="mt-1 text-[11.5px] text-stone-500">
              {organization
                ? 'Organizations are not scored: a firm’s deals are its people’s deals rolled up, so scoring both would count each deal twice.'
                : 'No score yet: a new contact, or nothing dated, has too little history to score.'}
            </p>
          ) : null}
          <button
            type="button"
            aria-expanded={rule}
            onClick={() => setRule((v) => !v)}
            className={clsx('mt-2 rounded text-[11.5px] text-amber-300 hover:text-amber-200', FOCUS)}
          >
            {rule ? 'Hide how this is decided' : 'How this is decided'}
          </button>
          {rule && relationship ? (
            <div className="mt-1.5 space-y-1.5 text-[11.5px] leading-snug text-stone-500">
              <p>{relationship.definitions.health.summary}</p>
              {relationship.definitions.stages.find((s) => s.key === row.stage) ? (
                <p>
                  {row.stageTitle}: {relationship.definitions.stages.find((s) => s.key === row.stage)?.rule}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

// ── Next best action ──────────────────────────────────────────────────────────

/** The one gold-outlined card. It states its rule and offers only things the platform does. */
export function NextBestActionCard({
  action,
  groups,
  emailLabel,
}: {
  action: NextAction
  groups: AddressGroups
  emailLabel: string
}) {
  const mailHref = mailtoHref(groups.primary)
  return (
    <section
      aria-label="Next best action"
      className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-amber-400/55 bg-amber-400/[0.05] px-5 py-4"
    >
      <div className="min-w-[260px] flex-1 space-y-1.5">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-amber-300">Next best action</div>
        <h3 className="font-display text-[26px] font-semibold leading-tight text-stone-50">{action.title}</h3>
        <p className="text-[13px] leading-snug text-stone-300">{action.body}</p>
        <p className="text-[11.5px] leading-snug text-stone-500">Why you see this: {action.rule}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => openDeal(action.dealId)}
          className={clsx(
            'inline-flex h-9 items-center rounded-lg bg-amber-400 px-4 text-[13px] font-semibold text-stone-950 hover:bg-amber-300',
            FOCUS,
          )}
        >
          Open {action.dealLabel.length > 28 ? 'the last deal' : action.dealLabel}
        </button>
        {mailHref ? (
          <a href={mailHref} className={QUIET_BUTTON + ' !h-9 !px-4 !text-[13px]'}>
            {emailLabel}
          </a>
        ) : null}
      </div>
    </section>
  )
}
